import { useEffect } from "react";
import { listenToEvent, removeListener, YantraWebsocketEvents } from "../utils/socket";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { YantraExecutionInfo } from "../tanstack/queries/player";

interface YantraTicket {
  id: string;
  status: "LOOSE" | "WIN";
  resultIndex: number;
  yantraExecutionId: string;
  totalPayout: number;
}

interface YantraExecution {
  id: string;
  status: string;
}

export type YantraStatus = "PLACE_BETS" | "COLLECTING_BETS" | "CALCULATED";

type WSListeners = {
  onExecutionUpdate?: (executionData: YantraExecutionInfo) => void;
  onExecutionTimer?: (
    yantraExecutionId: string,
    status: YantraStatus,
    timeLeft: number,
    untilMs: number
  ) => void;
  onTicketsUpdate?: (executionId: string, totalPayout: number) => void;
};

type Listener = {
  onExecutionUpdate?: (executionData: YantraExecutionInfo) => void;
  onExecutionTimer?: (
    yantraExecutionId: string,
    status: YantraStatus,
    timeLeft: number,
    untilMs: number
  ) => void;
  onTicketsUpdate?: (executionId: string, totalPayout: number) => void;
};

// Синглтон для хранения активных подписчиков
class WebSocketListeners {
  private static instance: WebSocketListeners;
  private listeners: Listener[] = [];
  private isInitialized = false;

  private constructor() {}

  static getInstance(): WebSocketListeners {
    if (!WebSocketListeners.instance) {
      WebSocketListeners.instance = new WebSocketListeners();
    }
    return WebSocketListeners.instance;
  }

  addListener(listener: Listener) {
    this.listeners.push(listener);
  }

  removeListener(listener: Listener) {
    this.listeners = this.listeners.filter((l) => l !== listener);
  }

  notifyExecutionUpdate(executionData: YantraExecutionInfo) {
    this.listeners.forEach((listener) => {
      listener.onExecutionUpdate?.(executionData);
    });
  }

  notifyExecutionTimer(
    yantraExecutionId: string,
    status: YantraStatus,
    timeLeft: number,
    untilMs: number
  ) {
    this.listeners.forEach((listener) => {
      listener.onExecutionTimer?.(yantraExecutionId, status, timeLeft, untilMs);
    });
  }

  notifyTicketsUpdate(executionId: string, totalPayout: number) {
    this.listeners.forEach((listener) => {
      listener.onTicketsUpdate?.(executionId, totalPayout);
    });
  }

  initialize(queryClient: any, navigate: any) {
    if (this.isInitialized) return;

    listenToEvent(YantraWebsocketEvents.updateYantras, () => {
      queryClient.invalidateQueries({ queryKey: ["executions"] });
    });

    listenToEvent(YantraWebsocketEvents.newYantras, () => {
      queryClient.invalidateQueries({ queryKey: ["executions"] });
    });

    listenToEvent(
      YantraWebsocketEvents.updateYantraTickets,
      (data: { tickets: Array<{ id: string; delta: YantraTicket }> }) => {
        data.tickets.forEach((ticket) => {
          this.notifyTicketsUpdate(ticket.delta.yantraExecutionId, ticket.delta.totalPayout);
        });
      }
    );

    listenToEvent(YantraWebsocketEvents.newYantraTickets, (_data: { tickets: YantraTicket[] }) => {
      queryClient.invalidateQueries({
        queryKey: ["playerHistory"],
        refetchType: "all",
      });
    });

    listenToEvent(
      YantraWebsocketEvents.updateYantraExecutions,
      (data: { executions: YantraExecutionInfo[] }) => {
        queryClient.invalidateQueries({
          queryKey: ["executions"],
          refetchType: "all",
        });
        data.executions.forEach((execution: YantraExecutionInfo) => {
          this.notifyExecutionUpdate(execution);
        });
      }
    );

    listenToEvent(
      YantraWebsocketEvents.newYantraExecutions,
      (_data: { executions: YantraExecution[] }) => {
        queryClient.invalidateQueries({ queryKey: ["executions"] });
      }
    );

    listenToEvent(YantraWebsocketEvents.forceLogout, (_data: { reason: string }) => {
      navigate("/error");
    });

    listenToEvent(
      YantraWebsocketEvents.yantraTimeChange,
      (data: {
        yantraExecutionId: string;
        leftMs: number;
        untilMs: number;
        status: YantraStatus;
      }) => {
        const number = Math.floor(data.leftMs / 1000);
        this.notifyExecutionTimer(data.yantraExecutionId, data.status, number, data.untilMs);
      }
    );

    this.isInitialized = true;
  }

  cleanup() {
    Object.values(YantraWebsocketEvents).forEach((ev) => removeListener(ev));
    this.isInitialized = false;
  }
}

export const useWebSocket = (listeners: WSListeners) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const wsListeners = WebSocketListeners.getInstance();
    wsListeners.initialize(queryClient, navigate);
    wsListeners.addListener(listeners);

    return () => {
      wsListeners.removeListener(listeners);
      // Очищаем все подписки только если больше нет активных слушателей
      if (wsListeners["listeners"].length === 0) {
        wsListeners.cleanup();
      }
    };
  }, [queryClient, navigate]);
};
